import type { PanderrTemplateDefinition } from "../types";

export const WELLNESS_MODERN_TEMPLATE: PanderrTemplateDefinition = {
  id: "wellness-modern",
  name: "Wellness Modern",
  description:
    "A calm, polished website for spas, salons, wellness studios, skincare, massage, and boutique wellness businesses.",
  category: "wellness",

  navigation: [
    { label: "Services", href: "/services/" },
    { label: "About", href: "/about/" },
    { label: "Experience", href: "/experience/" },
    { label: "Gallery", href: "/gallery/" },
    { label: "Contact", href: "/contact/" },
  ],

  headerCta: {
    label: "Book an Appointment",
    href: "/contact/",
    variant: "primary",
  },

  footer: {
    description:
      "Thoughtful care, relaxing experiences, and personalized wellness designed around you.",
    columns: [
      {
        heading: "Explore",
        links: [
          { label: "Services", href: "/services/" },
          { label: "About", href: "/about/" },
          { label: "Experience", href: "/experience/" },
        ],
      },
      {
        heading: "Visit",
        links: [
          { label: "Gallery", href: "/gallery/" },
          { label: "Contact", href: "/contact/" },
          { label: "Book an Appointment", href: "/contact/" },
        ],
      },
    ],
  },

  sections: [
    {
      id: "hero",
      type: "hero",
      eyebrow: "Care designed around you",
      heading: "Feel better. Slow down. Make space for yourself.",
      body: "Personalized wellness, thoughtful service, and a relaxing experience designed to help you look, feel, and live your best.",
      images: [
        {
          src: "/studio/assets/stock/images/minh-pham-OtXADkUh3-I-unsplash.jpg",
          alt: "Calm wellness interior",
          role: "hero",
        },
      ],
      buttons: [
        {
          label: "Explore Services",
          href: "/services/",
          variant: "primary",
        },
        {
          label: "Book an Appointment",
          href: "/contact/",
          variant: "secondary",
        },
      ],
    },

    {
      id: "trust",
      type: "trust",
      eyebrow: "Personal. Restorative. Thoughtful.",
      heading: "A better wellness experience starts with listening.",
      body: "Every visit should feel comfortable, unrushed, and tailored to what you need.",
    },

    {
      id: "services",
      type: "services",
      eyebrow: "Our services",
      heading: "Wellness services designed to help you feel your best.",
      body: "Customize these service areas around your treatments, appointments, programs, or wellness offerings.",
      services: [
        {
          title: "Signature Treatments",
          description:
            "Personalized treatments and services designed around your goals and preferences.",
          image: {
            src: "/studio/assets/stock/images/toa-heftiba-6bKpHAun4d8-unsplash.jpg",
            alt: "Relaxing wellness treatment setting",
            role: "service",
          },
          href: "/services/",
        },
        {
          title: "Skin & Body Care",
          description:
            "Thoughtful care focused on comfort, confidence, restoration, and healthy routines.",
          image: {
            src: "/studio/assets/stock/images/karolina-grabowska-ulh3-dLSXjI-unsplash.jpg",
            alt: "Wellness and body care",
            role: "service",
          },
          href: "/services/",
        },
        {
          title: "Wellness Experiences",
          description:
            "Programs, appointments, or services designed to support relaxation and ongoing wellbeing.",
          image: {
            src: "/studio/assets/stock/images/christiann-koepke-dQyS2pMYtok-unsplash.jpg",
            alt: "Calm wellness experience",
            role: "service",
          },
          href: "/experience/",
        },
      ],
    },

    {
      id: "about",
      type: "about",
      eyebrow: "Your wellness space",
      heading:
        "A calming environment. Thoughtful care. A more personal experience.",
      body: "We create experiences centered around comfort, expertise, and the belief that wellness should feel supportive—not rushed or complicated.",
      images: [
        {
          src: "/studio/assets/stock/images/inside-weather-Uxqlfigh6oE-unsplash.jpg",
          alt: "Peaceful wellness interior",
          role: "about",
        },
      ],
      buttons: [
        {
          label: "Learn About Us",
          href: "/about/",
          variant: "text",
        },
      ],
    },

    {
      id: "testimonials",
      type: "testimonials",
      eyebrow: "Client experiences",
      heading: "Care people look forward to returning to.",
      testimonials: [
        {
          quote:
            "The entire experience felt calm, thoughtful, and completely personalized.",
          name: "Client",
          detail: "Wellness appointment",
        },
        {
          quote:
            "Beautiful space, wonderful service, and exactly the reset I needed.",
          name: "Client",
          detail: "Signature treatment",
        },
      ],
    },

    {
      id: "gallery",
      type: "gallery",
      eyebrow: "Our space",
      heading: "Step into a more relaxing experience.",
      images: [
        {
          src: "/studio/assets/stock/images/minh-pham-OtXADkUh3-I-unsplash.jpg",
          alt: "Wellness space",
          role: "card",
        },
        {
          src: "/studio/assets/stock/images/inside-weather-Uxqlfigh6oE-unsplash.jpg",
          alt: "Relaxing interior",
          role: "card",
        },
        {
          src: "/studio/assets/stock/images/toa-heftiba-6bKpHAun4d8-unsplash.jpg",
          alt: "Wellness treatment space",
          role: "card",
        },
      ],
    },

    {
      id: "cta",
      type: "cta",
      eyebrow: "Ready for your next appointment?",
      heading: "Make time for yourself.",
      body: "Explore services, ask a question, or schedule the experience that's right for you.",
      buttons: [
        {
          label: "Book an Appointment",
          href: "/contact/",
          variant: "primary",
        },
      ],
    },
  ],

  pages: [
    {
      id: "services",
      name: "Services",
      title: "Services",
      slug: "/services/",
      sections: [
        {
          id: "services-hero",
          type: "hero",
          eyebrow: "Our services",
          heading: "Personalized services for the way you want to feel.",
          body: "Explore treatments, appointments, programs, and wellness services tailored to your needs.",
          images: [
            {
              src: "/studio/assets/stock/images/toa-heftiba-6bKpHAun4d8-unsplash.jpg",
              alt: "Wellness services",
              role: "hero",
            },
          ],
          buttons: [
            {
              label: "Book an Appointment",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },
        {
          id: "services-list",
          type: "services",
          eyebrow: "Choose your experience",
          heading: "Services designed around your goals.",
          services: [
            {
              title: "Signature Service",
              description:
                "Use this area for your most popular or defining treatment or appointment.",
              href: "/contact/",
            },
            {
              title: "Restorative Care",
              description:
                "Use this area for massage, skincare, recovery, relaxation, or restorative services.",
              href: "/contact/",
            },
            {
              title: "Ongoing Wellness",
              description:
                "Use this area for memberships, programs, packages, or recurring wellness services.",
              href: "/contact/",
            },
          ],
        },
        {
          id: "services-faq",
          type: "faq",
          eyebrow: "Before your appointment",
          heading: "Helpful information before you arrive.",
          faqs: [
            {
              question: "How do I choose the right service?",
              answer:
                "Use this answer to explain consultations, recommendations, or how clients select the right treatment.",
            },
            {
              question: "What should I expect at my appointment?",
              answer:
                "Describe arrival, preparation, appointment length, and what clients can expect during their visit.",
            },
            {
              question: "Do you offer packages or memberships?",
              answer:
                "Customize this answer with the packages, memberships, or ongoing care options you provide.",
            },
          ],
        },
      ],
    },

    {
      id: "about",
      name: "About",
      title: "About",
      slug: "/about/",
      sections: [
        {
          id: "about-hero",
          type: "hero",
          eyebrow: "About our practice",
          heading: "Wellness should feel personal.",
          body: "Introduce your team, philosophy, experience, and the approach that makes your business different.",
          images: [
            {
              src: "/studio/assets/stock/images/christiann-koepke-dQyS2pMYtok-unsplash.jpg",
              alt: "Wellness business",
              role: "hero",
            },
          ],
        },
        {
          id: "about-story",
          type: "about",
          eyebrow: "Our philosophy",
          heading: "Thoughtful care starts with understanding what you need.",
          body: "Use this section for your story, training, values, approach to care, or the experience you want every client to have.",
          images: [
            {
              src: "/studio/assets/stock/images/minh-pham-OtXADkUh3-I-unsplash.jpg",
              alt: "Peaceful wellness environment",
              role: "about",
            },
          ],
        },
        {
          id: "about-cta",
          type: "cta",
          eyebrow: "Come experience it",
          heading: "Ready to make wellness part of your routine?",
          buttons: [
            {
              label: "Book an Appointment",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },
      ],
    },

    {
      id: "experience",
      name: "Experience",
      title: "The Experience",
      slug: "/experience/",
      sections: [
        {
          id: "experience-hero",
          type: "hero",
          eyebrow: "Your visit",
          heading: "A more thoughtful experience from arrival to follow-up.",
          body: "Help clients understand what makes your environment, service, and approach feel different.",
          images: [
            {
              src: "/studio/assets/stock/images/inside-weather-Uxqlfigh6oE-unsplash.jpg",
              alt: "Relaxing wellness environment",
              role: "hero",
            },
          ],
        },
        {
          id: "experience-process",
          type: "process",
          eyebrow: "What to expect",
          heading: "Simple, comfortable, and centered around you.",
          steps: [
            {
              title: "Choose your service",
              description:
                "Explore your options or ask for guidance choosing the right appointment.",
            },
            {
              title: "Arrive and unwind",
              description:
                "Settle into a welcoming environment designed to help you feel comfortable.",
            },
            {
              title: "Leave feeling refreshed",
              description:
                "Enjoy thoughtful care, helpful recommendations, and a clear next step when appropriate.",
            },
          ],
        },
        {
          id: "experience-gallery",
          type: "gallery",
          eyebrow: "The atmosphere",
          heading: "A space designed to help you slow down.",
          images: [
            {
              src: "/studio/assets/stock/images/minh-pham-OtXADkUh3-I-unsplash.jpg",
              alt: "Wellness atmosphere",
              role: "card",
            },
            {
              src: "/studio/assets/stock/images/toa-heftiba-6bKpHAun4d8-unsplash.jpg",
              alt: "Wellness treatment setting",
              role: "card",
            },
            {
              src: "/studio/assets/stock/images/inside-weather-Uxqlfigh6oE-unsplash.jpg",
              alt: "Relaxing interior",
              role: "card",
            },
          ],
        },
      ],
    },

    {
      id: "gallery",
      name: "Gallery",
      title: "Gallery",
      slug: "/gallery/",
      sections: [
        {
          id: "gallery-hero",
          type: "hero",
          eyebrow: "Our space",
          heading: "A closer look at the experience.",
          body: "Showcase your treatment spaces, studio, salon, amenities, team, products, and atmosphere.",
          images: [
            {
              src: "/studio/assets/stock/images/minh-pham-OtXADkUh3-I-unsplash.jpg",
              alt: "Wellness gallery",
              role: "hero",
            },
          ],
        },
        {
          id: "gallery-images",
          type: "gallery",
          eyebrow: "Gallery",
          heading: "Designed for comfort, care, and a little breathing room.",
          images: [
            {
              src: "/studio/assets/stock/images/minh-pham-OtXADkUh3-I-unsplash.jpg",
              alt: "Wellness interior",
              role: "card",
            },
            {
              src: "/studio/assets/stock/images/inside-weather-Uxqlfigh6oE-unsplash.jpg",
              alt: "Relaxing treatment space",
              role: "card",
            },
            {
              src: "/studio/assets/stock/images/toa-heftiba-6bKpHAun4d8-unsplash.jpg",
              alt: "Treatment environment",
              role: "card",
            },
            {
              src: "/studio/assets/stock/images/christiann-koepke-dQyS2pMYtok-unsplash.jpg",
              alt: "Wellness detail",
              role: "card",
            },
          ],
        },
      ],
    },

    {
      id: "contact",
      name: "Contact",
      title: "Contact",
      slug: "/contact/",
      sections: [
        {
          id: "contact-hero",
          type: "hero",
          eyebrow: "Book your visit",
          heading: "Ready to make time for yourself?",
          body: "Ask a question, choose a service, or schedule your next appointment.",
          images: [
            {
              src: "/studio/assets/stock/images/inside-weather-Uxqlfigh6oE-unsplash.jpg",
              alt: "Welcoming wellness space",
              role: "hero",
            },
          ],
        },
        {
          id: "contact-details",
          type: "contact",
          eyebrow: "Get in touch",
          heading: "We'd love to hear from you.",
          body: "Update your phone number, email, address, hours, booking information, and appointment details in Studio.",
        },
        {
          id: "contact-faq",
          type: "faq",
          eyebrow: "Appointment questions",
          heading: "A few things clients often ask.",
          faqs: [
            {
              question: "Do I need an appointment?",
              answer:
                "Customize this answer with your appointment and walk-in policies.",
            },
            {
              question: "What are your hours?",
              answer:
                "Add your business hours and appointment availability here.",
            },
            {
              question: "How do I reschedule?",
              answer:
                "Explain your rescheduling and cancellation policies here.",
            },
          ],
        },
      ],
    },
  ],
};
